import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { InfoItem, ResponsiveService } from '@frk/eds-components';
import { SalesChargeBreakPoint, Yields } from '@models';
import { EMDASH } from '@products/utils/constants/product.constants';
import { SiteConfigService } from '@services/site-config.service';
import { TranslateService } from '@shared/translate/translate.service';
import { ConfigurationId, FundId } from '@types';
import { Observable } from 'rxjs';

@Component({
  selector: 'ft-rates-and-yields',
  templateUrl: './rates-and-yields.component.html',
  styleUrls: ['./rates-and-yields.component.scss'],
})
export class RatesAndYieldsComponent implements OnInit {
  @Input() salesChargeBreakPoints: SalesChargeBreakPoint[];
  @Input() yields: Yields;
  @Input() productType: ConfigurationId;
  @Input() fundId: FundId;
  @Input() navDate: string;

  rateBreakPoints: InfoItem;
  mobileRateBreakPoints: InfoItem[] = [];
  isHandheld$: Observable<boolean>;
  yieldsInfo: Record<string, InfoItem> = {};

  // NGC-7949: Suppression list for rates and breakpoints section against product type.
  private readonly ratesBreakpointsSuppressList: ConfigurationId[] = [
    ConfigurationId.CEF,
    ConfigurationId.ETF,
  ];
  showRatesBreakpointsSection = false;

  private placements: Record<
    number,
    (productType: ConfigurationId) => string
  > = {
    8012: () => 'RateAndBreakpointsNav',
    8023: (productType: ConfigurationId) =>
      [ConfigurationId.CEF, ConfigurationId.ETF].includes(productType)
        ? 'RateAndBreakpointsMarketPrice'
        : 'RateAndBreakpointsPop',
  };

  private readonly yieldNames = [
    'disrate',
    'standard',
    'taxequivalent',
    'taxequivalentdisrate',
    'current',
    'effective',
  ];

  constructor(
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();

    // NGC-7949: Check & assign visibility condition for rates breakpoints.
    if (
      !this.ratesBreakpointsSuppressList.includes(this.productType) &&
      this.salesChargeBreakPoints?.length
    ) {
      this.rateBreakPoints = this.getBreakPointInfoItems(
        this.salesChargeBreakPoints,
        false
      ) as InfoItem;
      this.mobileRateBreakPoints = this.getBreakPointInfoItems(
        this.salesChargeBreakPoints,
        true
      ) as InfoItem[];

      // Now check, if values are still empty after filter.
      this.showRatesBreakpointsSection =
        this.rateBreakPoints.values.length !== 0;
    }

    // NGC-7956: Rates and Yields section is blank
    this.yieldNames.forEach((key) => {
      if (this.showYieldInfo(key)) {
        this.yieldsInfo[key] = this.getYieldInfo(key);
      }
    });

    this.changeDetector.detectChanges();
  }

  /**
   * Get InfoItem(s) for Rates and Breakpoints as per view type.
   * @param salesChargeBreakPoints filtered sales charge break points
   * @param isHandheld boolean value
   */
  private getBreakPointInfoItems(
    salesChargeBreakPoints: SalesChargeBreakPoint[],
    isHandheld: boolean
  ): InfoItem | InfoItem[] {
    const breakPoint: InfoItem[] = salesChargeBreakPoints
      .filter(
        (salesChargeBreakPoint) =>
          salesChargeBreakPoint.dataCategory ===
            'DistributionRateBreakPoints' &&
          salesChargeBreakPoint.breakPointTypeKey !== 8012 // NGC-9575: Remove "Distribution at NAV" from "Rate & Breakpoints".
      )
      .sort((a, b) => {
        return a.breakPointTypeKey - b.breakPointTypeKey;
      })
      .map((data) => {
        return {
          label: data.breakPointYearPercentage,
          val: data.distributionRateBreakPoint,
          caveatPlacement:
            data.breakPointTypeKey in this.placements
              ? `Distribution:${this.placements[data.breakPointTypeKey](
                  this.productType
                )}`
              : null,
        };
      });

    return isHandheld
      ? breakPoint
      : {
          label: this.translateService.instant(
            'products.distribution-rate-and-breakpoints'
          ),
          values: breakPoint,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: salesChargeBreakPoints[0].asOfDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-daily'
          )})`,
          caveatPlacement: 'Distribution:RateAndBreakpoints',
          tooltip: this.translateService.tooltip(
            'products.distribution-rate-and-breakpoints'
          ),
        };
  }

  /**
   * Return boolean value to show respective yield or not.
   * Included condition of EMDASH as well.
   * @param yieldName 'disrate' / 'standard' / 'taxequivalent' / 'taxequivalentdisrate' / 'current' / 'effective'
   */
  private showYieldInfo(yieldName: string): boolean {
    switch (yieldName) {
      case 'disrate': {
        let distributionRateBreakPoint: string;

        if (this.salesChargeBreakPoints?.length) {
          distributionRateBreakPoint = this.salesChargeBreakPoints.find(
            (salesChargeBreakPoint) =>
              [8012, 8023].includes(salesChargeBreakPoint.breakPointTypeKey) // 8012: NAV, 8023: POP/MarketPrice
          )?.distributionRateBreakPoint;
        }

        return distributionRateBreakPoint &&
          distributionRateBreakPoint !== EMDASH
          ? true
          : false;
      }

      case 'standard':
        return (this.yields?.secYieldThrtyDay &&
          this.yields.secYieldThrtyDay !== EMDASH) ||
          (this.yields?.secYieldThrtyDayWoWaiver &&
            this.yields.secYieldThrtyDayWoWaiver !== EMDASH)
          ? true
          : false;

      case 'taxequivalent':
        return (this.yields?.taxableEquivalentYield &&
          this.yields.taxableEquivalentYield !== EMDASH) ||
          (this.yields?.taxableEquivalentYieldWithoutWaiver &&
            this.yields.taxableEquivalentYieldWithoutWaiver !== EMDASH)
          ? true
          : false;

      case 'taxequivalentdisrate': {
        let distributionRateBreakPoint: string;

        if (this.salesChargeBreakPoints?.length) {
          distributionRateBreakPoint = this.salesChargeBreakPoints.find(
            (salesChargeBreakPoint) =>
              salesChargeBreakPoint.breakPointTypeKey === 9999
          )?.distributionRateBreakPoint;
        }

        return distributionRateBreakPoint &&
          distributionRateBreakPoint !== EMDASH
          ? true
          : false;
      }

      case 'current':
        return (this.yields?.currentYield7Day &&
          this.yields.currentYield7Day !== EMDASH) ||
          (this.yields?.currentYield7DayWithoutWaiver &&
            this.yields.currentYield7DayWithoutWaiver !== EMDASH)
          ? true
          : false;

      case 'effective':
        return (this.yields?.effectiveYield7Day &&
          this.yields.effectiveYield7Day !== EMDASH) ||
          (this.yields?.effectiveYield7DayWithoutWaiver &&
            this.yields.effectiveYield7DayWithoutWaiver !== EMDASH)
          ? true
          : false;
    }
  }

  /**
   * Get InfoItem(s) for Yields as per view type.
   * @param yieldName 'disrate' / 'standard' / 'taxequivalent'/ 'taxequivalentdisrate' / 'current' / 'effective'
   * @param isHandheld optional boolean value
   */
  private getYieldInfo(yieldName: string): InfoItem {
    switch (yieldName) {
      case 'disrate': {
        const values: InfoItem[] = [];
        let salesChargeBreakPointNAVData: SalesChargeBreakPoint;
        let salesChargeBreakPointPOPMPData: SalesChargeBreakPoint;

        if (this.salesChargeBreakPoints?.length) {
          salesChargeBreakPointNAVData = this.salesChargeBreakPoints.find(
            (salesChargeBreakPoint) =>
              salesChargeBreakPoint.breakPointTypeKey === 8012 // NAV breakpoint
          );

          salesChargeBreakPointPOPMPData = this.salesChargeBreakPoints.find(
            (salesChargeBreakPoint) =>
              salesChargeBreakPoint.breakPointTypeKey === 8023 // Market Price breakpoint
          );
        }

        // If Nav breakpoint available, push it to values.
        if (salesChargeBreakPointNAVData) {
          values.push({
            label: this.translateService.instant(
              'products.distribution-yield-disrate-nav-label'
            ),
            val: salesChargeBreakPointNAVData.distributionRateBreakPoint,
            caveatPlacement: 'Distribution:YieldDisrateNav',
          });
        }

        // If Market price breakpoint is available, then again check for product type and push label accordingly.
        if (
          salesChargeBreakPointPOPMPData &&
          this.siteConfigService.isTraded(this.fundId)
        ) {
          if (
            [
              ConfigurationId.MF,
              ConfigurationId.TIF,
              ConfigurationId.VIP,
              ConfigurationId._529,
              ConfigurationId.CIT,
            ].includes(this.productType)
          ) {
            values.push({
              label: this.translateService.instant(
                'products.distribution-yield-disrate-pop-label'
              ),
              val: salesChargeBreakPointNAVData.distributionRateBreakPoint,
              caveatPlacement: 'Distribution:YieldDisratePop',
            });
          } else if (
            [ConfigurationId.ETF, ConfigurationId.CEF].includes(
              this.productType
            )
          ) {
            values.push({
              label: this.translateService.instant(
                'products.distribution-yield-disrate-mp-label'
              ),
              val: salesChargeBreakPointPOPMPData.distributionRateBreakPoint,
              caveatPlacement: 'Distribution:YieldDisrateMarketPrice',
            });
          }
        }

        return {
          label: this.translateService.instant(
            'products.distribution-yield-disrate-title'
          ),
          values,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: this.salesChargeBreakPoints[0].asOfDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-daily'
          )})`,
          caveatPlacement: 'Distribution:YieldDisrate',
          tooltip: this.translateService.tooltip(
            'products.distribution-yield-disrate'
          ),
        };
      }

      case 'standard': {
        const values: InfoItem[] = [];

        if (
          this.yields.secYieldThrtyDay &&
          this.yields.secYieldThrtyDay !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.with-waiver'),
            val: this.yields.secYieldThrtyDay,
          });
        }
        if (
          this.yields.secYieldThrtyDayWoWaiver &&
          this.yields.secYieldThrtyDayWoWaiver !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.without-waiver'),
            val: this.yields.secYieldThrtyDayWoWaiver,
          });
        }

        return {
          // NGC-9847: For Closed End Funds change label to show 'current yield'
          label:
            this.productType === ConfigurationId.CEF
              ? this.translateService.instant(
                  'products.statistic-CURRENT_YIELD_WGT_AVG'
                )
              : this.translateService.instant(
                  'products.distribution-yield-standard-title'
                ),
          values,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: this.yields.stdYieldThirtyDayAsofDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-monthly'
          )})`,
          caveatPlacement: 'Distribution:YieldStandard',
          tooltip: this.translateService.tooltip(
            'products.distribution-yield-standard'
          ),
        };
      }

      case 'taxequivalent': {
        const values: InfoItem[] = [];

        if (
          this.yields.taxableEquivalentYield &&
          this.yields.taxableEquivalentYield !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.with-waiver'),
            val: this.yields.taxableEquivalentYield,
          });
        }
        if (
          this.yields.taxableEquivalentYieldWithoutWaiver &&
          this.yields.taxableEquivalentYieldWithoutWaiver !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.without-waiver'),
            val: this.yields.taxableEquivalentYieldWithoutWaiver,
          });
        }

        return {
          label: this.translateService.instant(
            'products.distribution-yield-taxequivalent-title'
          ),
          values,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: this.yields.stdYieldThirtyDayAsofDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-monthly'
          )})`,
          caveatPlacement: 'Distribution:YieldTaxEquivalent',
          tooltip: this.translateService.tooltip(
            'products.distribution-yield-taxequivalent'
          ),
        };
      }

      case 'taxequivalentdisrate': {
        return {
          label: this.translateService.instant(
            'products.distribution-yield-taxequivalentdisrate-title'
          ),
          val: this.salesChargeBreakPoints.find(
            (salesChargeBreakPoint) =>
              salesChargeBreakPoint.breakPointTypeKey === 9999
          )?.distributionRateBreakPoint,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: this.salesChargeBreakPoints[0].asOfDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-daily'
          )})`,
          caveatPlacement: 'Distribution:YieldTaxEquivalentDisrate',
          tooltip: this.translateService.tooltip(
            'products.distribution-yield-taxequivalentdisrate'
          ),
        };
      }

      case 'current': {
        const values: InfoItem[] = [];

        if (
          this.yields.currentYield7Day &&
          this.yields.currentYield7Day !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.with-waiver'),
            val: this.yields.currentYield7Day,
          });
        }
        if (
          this.yields.currentYield7DayWithoutWaiver &&
          this.yields.currentYield7DayWithoutWaiver !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.without-waiver'),
            val: this.yields.currentYield7DayWithoutWaiver,
          });
        }

        return {
          label: this.translateService.instant(
            'products.distribution-yield-current-title'
          ),
          values,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: this.navDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-daily'
          )})`,
          caveatPlacement: 'Distribution:YieldCurrent',
          tooltip: this.translateService.tooltip(
            'products.distribution-yield-current'
          ),
        };
      }

      case 'effective': {
        const values: InfoItem[] = [];

        if (
          this.yields.effectiveYield7Day &&
          this.yields.effectiveYield7Day !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.with-waiver'),
            val: this.yields.effectiveYield7Day,
          });
        }
        if (
          this.yields.effectiveYield7DayWithoutWaiver &&
          this.yields.effectiveYield7DayWithoutWaiver !== EMDASH
        ) {
          values.push({
            label: this.translateService.instant('products.without-waiver'),
            val: this.yields.effectiveYield7DayWithoutWaiver,
          });
        }

        return {
          label: this.translateService.instant(
            'products.distribution-yield-effective-title'
          ),
          values,
          asOfLabel: this.translateService.instant('products.as-of'),
          asOfDate: this.navDate,
          updateFrequency: `(${this.translateService.instant(
            'products.updated-daily'
          )})`,
          caveatPlacement: 'Distribution:YieldEffective',
          tooltip: this.translateService.tooltip(
            'products.distribution-yield-effective'
          ),
        };
      }
    }
  }

  /**
   * Get tooltip content for Yields.
   * @param value string
   */
  getTooltipContent(value: string): string | undefined {
    return this.translateService.tooltip(value);
  }
}
